import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@rtkwlf/c3-core-react';
import { Badge, Tag } from '@rtkwlf/fenrir-react';
import { faExternalLink } from '@rtkwlf/pro-solid-svg-icons/faExternalLink';
import { type Spidr } from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';

import { useGetOrganizationOptions } from '@apis/track-public/query-options';

import { ConciergeTierBadge } from '../../../../../components/concierge-tier-badge/concierge-tier-badge';
import { useFormatNullableDate } from '../../../../../components/formatted-date';
import { routes } from '../../../../../constants/routes';
import type { spidrStateToTimeline } from '../../../../../constants/spidr-state-to-appearance';
import { spidrStateToBadge } from '../../../../../constants/spidr-state-to-appearance';
import { useSecurityJourneyTranslations } from '../../../../../locales/use-security-journey-translations';

import { SecurityFocusTimelineItemInfo } from './security-focus-timeline-item-info';
import styles from './security-focus-timeline-spidr.module.css';

type SecurityFocusTimelineSpidrContentProps = {
  adhoc?: boolean;
  expiryDate: Spidr['expiryDate'];
  name: Spidr['blueprint']['name'];
  state: Spidr['state'];
  ticketId: Spidr['ticketId'];
  tier: Spidr['blueprint']['tier'];
};

export const SecurityFocusTimelineSpidrContent: React.FunctionComponent<
  SecurityFocusTimelineSpidrContentProps
> = ({ adhoc = false, expiryDate, name, state, ticketId, tier }) => {
  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  const { data: organization } = useQuery(useGetOrganizationOptions());

  const isOrganizationTierNonLegacy = React.useMemo(() => {
    return organization?.tierInfo && organization.tierInfo.legacy === false;
  }, [organization]);

  /**
   * Get the badge appearance
   */
  const badgeAppearance =
    spidrStateToBadge[state as keyof typeof spidrStateToTimeline];

  return (
    <>
      <header className={styles['sft-spidr-item-header']}>
        <div className={styles['sft-spidr-item-header-details']}>
          <Text
            as="div"
            size="3"
            weight="bold"
            className={styles['sft-spidr-item-content-name']}
          >
            {name}
          </Text>
          <div className={styles['sft-spidr-item-badge-container']}>
            <Badge.Root appearance={badgeAppearance.appearance}>
              <Badge.Icon>
                <FontAwesomeIcon icon={badgeAppearance.icon} />
              </Badge.Icon>
              <Badge.Text>{t(badgeAppearance.text)}</Badge.Text>
            </Badge.Root>
            {ticketId === 'internal' ? (
              <Text className={styles['sft-spidr-item-zendesk-link']}>
                {t('focusID.timeline.spidrs.internalOnly')}
              </Text>
            ) : (
              ticketId && (
                <a
                  className={styles['sft-spidr-item-zendesk-link']}
                  href={routes.tickets.ticketID.index({ ticketID: ticketId })}
                  target="__blank"
                >
                  {t('focusID.timeline.spidrs.viewTicket')}
                  <FontAwesomeIcon icon={faExternalLink} />
                </a>
              )
            )}
          </div>
        </div>
        {adhoc && (
          <Tag variant="secondary" appearance="neutral">
            {t('focusID.timeline.spidrs.adhoc')}
          </Tag>
        )}
      </header>
      <div className={styles['sft-spidr-item-content-details']}>
        {isOrganizationTierNonLegacy && (
          <div className={styles['sft-spidr-item-tier']}>
            <Text size="2" weight="bold">
              {t('focusID.timeline.tier')}
            </Text>
            <ConciergeTierBadge.Spidr tier={tier} />
          </div>
        )}
        <SecurityFocusTimelineItemInfo
          heading={t('focusID.timeline.spidrs.expiry')}
          value={useFormatNullableDate(expiryDate)}
        />
      </div>
    </>
  );
};
